import Row from 'components/core/Row'
import { WidgetComponentProps } from '../../wilsonChat.types'
import { useState } from 'react'
import {
    SendEmailFormErrors,
    SendEmailFormValues,
} from 'components/composite/SendEmailForm/SendEmailForm'
import { showToastError } from 'hooks/useToast'
import { WIDGET_KEY } from '../widget.constants'
import Button from 'components/core/Button'
import { getEmailsArrFromEmailStr } from 'utils/email.utils'
import Column from 'components/core/Column'
import Typography from 'components/core/Typography'
import Switch from 'components/core/Switch'
import { ThreadSendEmailForm } from 'components/composite/ThreadSendEmailForm'
import { ThreadSendEmailFormReadOnly } from 'components/composite/ThreadSendEmailFormReadOnly'
import { Doc } from 'types/doc.types'
import { formatDateWithTZ } from 'utils/date.utils'

export type SendEmailWidgetData = {
    isFromDisabled?: boolean
    customerId?: string
    orderIds?: string[] | null
    status?: 'sent' | 'cancelled'
    shouldSendSeparateEmails: boolean
    replyToExternalMessageId?: string | null
    documents: Doc[]
    emailType?: 'partner' | 'shipper' | 'consignee' | 'customsBroker' | null

    // I'm making string[] | string for backwards compatibility with older messages. Maybe in the future we can remove this
    to?: string[] | string
    cc?: string[] | string
    bcc?: string[] | string
    from: string
    subject: string
    htmlContent: string
    scheduledAt?: string | null
    scheduledAtTimezone?: string | null
    extraOptions?: {
        initialProcurementEmail?: boolean
    }
}

export type SendEmailWidgetProps = WidgetComponentProps<
    'SendEmail',
    SendEmailWidgetData
>

export const SendEmailWidget = ({
    chatId,
    messageId,
    widgetData,
    onSendMessage,
}: SendEmailWidgetProps) => {
    const isReply = !!widgetData.replyToExternalMessageId

    // necessary for backwards compatibility with older messages
    const defaultTo = Array.isArray(widgetData.to)
        ? widgetData.to.join(', ')
        : widgetData.to ?? ''
    const defaultCc = Array.isArray(widgetData.cc)
        ? widgetData.cc.join(', ')
        : widgetData.cc ?? ''
    const defaultBcc = Array.isArray(widgetData.bcc)
        ? widgetData.bcc.join(', ')
        : widgetData.bcc ?? ''

    const [to, setTo] = useState(defaultTo)
    const [cc, setCc] = useState(defaultCc)
    const [bcc, setBcc] = useState(defaultBcc)

    const [mutation, setMutation] = useState(widgetData)
    const [errors, setErrors] = useState<SendEmailFormErrors>({})
    const [htmlContent, setHtmlContent] = useState<string>(
        widgetData.htmlContent
    )
    const [shouldSendSeparateEmails, setShouldSendSeparateEmails] = useState(
        isReply ? false : !!widgetData.shouldSendSeparateEmails
    )

    const _onValidate = (data: Partial<typeof mutation>) => {
        const errors: SendEmailFormErrors = {}

        const { emails: toEmails, isValid: isToEmailsValid } =
            getEmailsArrFromEmailStr(to)
        if (!toEmails.length) {
            errors.to = 'Required'
        }
        if (!isToEmailsValid) {
            errors.to = 'Invalid email(s)'
        }
        if (!data.from) {
            errors.from = 'Required'
        }
        if (!data.subject) {
            errors.subject = 'Required'
        }
        if (!data.htmlContent) {
            errors.subject = 'Required'
        }

        const { isValid: isCcEmailsValid } = getEmailsArrFromEmailStr(cc)
        if (!isCcEmailsValid) {
            errors.cc = 'Invalid email(s)'
        }

        const { isValid: isBccEmailsValid } = getEmailsArrFromEmailStr(bcc)
        if (!isBccEmailsValid) {
            errors.bcc = 'Invalid email(s)'
        }

        if (!Object.keys(errors).length) {
            setErrors({})
            return
        }

        setErrors(errors)
        return errors
    }

    const _onSend = () => {
        const errors = _onValidate({ ...mutation, htmlContent })
        if (errors) {
            showToastError('Please fix the errors before proceeding')
            return
        }

        onSendMessage({
            message: isReply ? '[Action: ReplyEmail]' : '[Action: SendEmail]',
            payload: {
                replyToWidget: WIDGET_KEY.sendEmail,
                replyToMessageId: messageId,
                widgetData: {
                    ...mutation,
                    to: getEmailsArrFromEmailStr(to).emails,
                    cc: getEmailsArrFromEmailStr(cc).emails,
                    bcc: getEmailsArrFromEmailStr(bcc).emails,
                    htmlContent,
                    shouldSendSeparateEmails,
                    status: 'sent',
                },
            },
        })
    }

    const _onCancel = () => {
        onSendMessage({
            message: '[Action: CancelEmail]',
            payload: {
                replyToWidget: WIDGET_KEY.sendEmail,
                replyToMessageId: messageId,
                widgetData: {
                    ...mutation,
                    to: getEmailsArrFromEmailStr(to).emails,
                    cc: getEmailsArrFromEmailStr(cc).emails,
                    bcc: getEmailsArrFromEmailStr(bcc).emails,
                    htmlContent,
                    shouldSendSeparateEmails,
                    status: 'cancelled',
                },
            },
        })
    }

    const _onChangeSelectedThreadDocuments = (documents: Doc[]) => {
        setMutation((prev) => ({ ...prev, documents }))
    }

    const _onChangeState = (key: keyof SendEmailFormValues, value: string) => {
        if (key === 'htmlContent') {
            setHtmlContent(value)

            // stop right away since there is no error for htmlContent
            // moreover an update on state will cause widger re-render
            // that will move cursor automatically to the beginning of the
            // HTMLEditor component
            return
        } else if (key === 'to') {
            setTo(value)
        } else if (key === 'cc') {
            setCc(value)
        } else if (key === 'bcc') {
            setBcc(value)
        } else {
            setMutation((prev) => ({ ...prev, [key]: value }))
        }

        setErrors((prev) => ({ ...prev, [key]: '' }))
    }

    if (widgetData.status) {
        let label = ''

        if (widgetData.status === 'sent') {
            if (mutation.scheduledAt) {
                label = `The email is scheduled to be sent on ${formatDateWithTZ(
                    {
                        date: mutation.scheduledAt,
                        timeZone: mutation.scheduledAtTimezone,
                    }
                )}`
            } else {
                label = 'Sent'
            }
        } else {
            label = 'Cancelled draft email'
        }

        return (
            <Column gap="p1" p="p2">
                <ThreadSendEmailFormReadOnly
                    isShowSubjectField={!isReply}
                    threadId={chatId}
                    to={to}
                    cc={cc}
                    bcc={bcc}
                    subject={mutation.subject}
                    htmlContent={htmlContent}
                    documents={mutation.documents ?? []}
                />
                <Row gap="p1" justifyContent="flex-end">
                    <Typography isItalic variant="body2" color="secondary">
                        {label}
                    </Typography>
                </Row>
            </Column>
        )
    }

    return (
        <Column gap="p1" p="p2">
            <ThreadSendEmailForm
                isFromDisabled
                isShowFromField={!isReply}
                isShowSubjectField={!isReply}
                customerId={widgetData.customerId}
                threadId={chatId}
                from={mutation.from}
                to={to}
                cc={cc}
                bcc={bcc}
                subject={mutation.subject}
                htmlContent={htmlContent}
                errors={errors}
                documents={mutation.documents ?? []}
                onChange={_onChangeState}
                onChangeSelectedThreadDocuments={
                    _onChangeSelectedThreadDocuments
                }
            />
            {!!mutation.scheduledAt && (
                <Row gap="p1" justifyContent="flex-end">
                    <Typography isItalic variant="body2" color="secondary">
                        {`The email is scheduled to be sent on ${formatDateWithTZ(
                            {
                                date: mutation.scheduledAt,
                                timeZone: mutation.scheduledAtTimezone,
                            }
                        )}`}
                    </Typography>
                </Row>
            )}
            <Row gap="p1" justifyContent="space-between" alignItems="center">
                <Row gap="p1" justifyContent="flex-start">
                    <Switch
                        isChecked={shouldSendSeparateEmails}
                        onChange={setShouldSendSeparateEmails}
                    />
                    <Typography color="primary" variant="body2">
                        Send unique emails to each recipient
                    </Typography>
                </Row>

                <Row gap="p1" justifyContent="flex-end">
                    <Button variant="secondary" onClick={_onCancel}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={_onSend}>
                        Send
                    </Button>
                </Row>
            </Row>
        </Column>
    )
}
